import axios from 'axios';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://data.axionenterprise.cloud/api';

// Dados mock para desenvolvimento
const mockData = {
  analytics: {
    sales: {
      totalSales: 125000,
      monthlySales: [
        { month: 'Jan', sales: 15000 },
        { month: 'Fev', sales: 18000 },
        { month: 'Mar', sales: 22000 },
        { month: 'Abr', sales: 20000 },
        { month: 'Mai', sales: 25000 },
        { month: 'Jun', sales: 25000 }
      ],
      topProducts: [
        { name: 'Software Pro', sales: 45000 },
        { name: 'Consultoria', sales: 32000 },
        { name: 'Treinamento', sales: 28000 }
      ],
      conversionRate: 3.2
    }
  },
  admin: {
    users: [
      {
        id: 'admin-001',
        name: 'Admin User',
        email: 'admin@axion.com',
        role: 'admin',
        createdAt: '2024-01-01T00:00:00Z',
        lastLogin: '2026-01-06T23:00:00Z'
      },
      {
        id: 'user-001',
        name: 'João Silva',
        email: 'joao@empresa.com',
        role: 'user',
        createdAt: '2024-02-15T00:00:00Z',
        lastLogin: '2026-01-06T10:30:00Z'
      }
    ]
  },
  generate: {
    pitch: {
      pitch: "Apresentamos a solução definitiva para gestão empresarial! Nosso software revoluciona sua produtividade com recursos inovadores e interface intuitiva. Transforme seu negócio hoje mesmo com resultados comprovados e suporte 24/7."
    },
    image: {
      imageUrl: "https://picsum.photos/seed/axion/1024/1024.jpg",
      imageId: "img_001"
    },
    videoScript: {
      script: "Abertura: Apresentação do problema (30s)\\nSolução: Demonstração do software (60s)\\nBenefícios: Depoimentos e resultados (45s)\\nCTA: Chamada para ação (15s)",
      estimatedDuration: 150
    }
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': process.env.AXION_API_KEY || process.env.GEMINI_API_KEY || 'AIzaSyB6YFIz2viJLKCxl9vuT0g1VkdH6QmuLdQ'
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros e usar fallback mock
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Se for erro de autenticação, limpar token
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    // Se for erro de API key inválida ou servidor indisponível, usar mock
    if (error.response?.status === 401 || error.response?.status >= 500) {
      console.warn('🔄 API indisponível, usando dados mock...');
      
      // Retornar dados mock baseado na URL
      const url = error.config?.url || '';
      if (url.includes('/analytics/sales')) {
        return Promise.resolve({ data: mockData.analytics.sales });
      }
      if (url.includes('/admin/users')) {
        return Promise.resolve({ data: mockData.admin.users });
      }
      if (url.includes('/generate/pitch')) {
        return Promise.resolve({ data: mockData.generate.pitch });
      }
      if (url.includes('/generate/image')) {
        return Promise.resolve({ data: mockData.generate.image });
      }
      if (url.includes('/generate/video-script')) {
        return Promise.resolve({ data: mockData.generate.videoScript });
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Serviços de API
export const authService = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  logout: async () => {
    await api.post('/auth/logout');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },
};

export const generateService = {
  pitch: async (data: { prompt: string; tone: string; audience: string }) => {
    const response = await api.post('/generate/pitch', data);
    return response.data;
  },
  
  image: async (data: { prompt: string; style: string; size: string }) => {
    const response = await api.post('/generate/image', data);
    return response.data;
  },
  
  videoScript: async (data: { topic: string; duration: string; style: string }) => {
    const response = await api.post('/generate/video-script', data);
    return response.data;
  },
  
  summarize: async (data: { text: string; length: string }) => {
    const response = await api.post('/generate/summarize', data);
    return response.data;
  },
  
  translate: async (data: { text: string; from: string; to: string }) => {
    const response = await api.post('/generate/translate', data);
    return response.data;
  },
  
  code: async (data: { prompt: string; language: string }) => {
    const response = await api.post('/generate/code', data);
    return response.data;
  },
};

export const analysisService = {
  sentiment: async (data: { text: string }) => {
    const response = await api.post('/analyze/sentiment', data);
    return response.data;
  },
  
  osint: async (data: { target: string; type: string }) => {
    const response = await api.post('/osint/analysis', data);
    return response.data;
  },
};

export const marketingService = {
  adsCampaign: async (data: { product: string; target: string; budget: string; platform: string }) => {
    const response = await api.post('/marketing/ads-campaign', data);
    return response.data;
  },
  
  emailSequence: async (data: { goal: string; audience: string; numberOfEmails: number }) => {
    const response = await api.post('/marketing/email-sequence', data);
    return response.data;
  },
  
  salesFunnel: async (data: { product: string; targetAudience: string; stages: string[] }) => {
    const response = await api.post('/marketing/sales-funnel', data);
    return response.data;
  },
  
  landingPage: async (data: { product: string; target: string; goal: string }) => {
    const response = await api.post('/marketing/landing-page', data);
    return response.data;
  },
  
  viralContent: async (data: { topic: string; platform: string; format: string }) => {
    const response = await api.post('/marketing/viral-content', data);
    return response.data;
  },
  
  blackFriday: async (data: { product: string; discount: number; urgency: boolean }) => {
    const response = await api.post('/marketing/black-friday', data);
    return response.data;
  },
};

export const analyticsService = {
  sales: async () => {
    const response = await api.get('/analytics/sales');
    return response.data;
  },
};

export const adminService = {
  getUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  
  createUser: async (data: { name: string; email: string; password: string; role: string }) => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },
  
  updateUser: async (id: string, data: { name: string; email: string; role: string }) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },
  
  deleteUser: async (id: string) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },
};
